import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useCallback, useEffect, useState } from 'react'
import { CreateButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import {
  FeaturesConfigFormValues,
  FeaturesConfigurationFeaturesForm,
} from 'src/screens/administration/configuration-management/features-configuration/features/edit/FeaturesConfigurationFeaturesForm'
import {
  Feature_Config_Insert_Input,
  Feature_Group_Config,
  Feature_Type_Config,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import { CONFIGURATION_STATUS, FEATURE_CONFIG_SELECTION_TYPE } from 'src/shared/constants/constants'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import { Client, useClient } from 'urql'
import {
  fetchAllFeatureGroups,
  fetchAllFeatureTypesForFeaturesForm,
  initialKapFactsheetLevelRequiresKpi,
} from 'src/screens/administration/configuration-management/features-configuration/features/edit/FeaturesConfigurationFeaturesFormUtils'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import {
  insertFeatureConfig,
  queryHighestFeatureConfigSortNumber,
} from 'src/screens/administration/configuration-management/features-configuration/features/featuresConfigQueries'
import { useUserLocale } from 'src/user/UserContext'

const defaultInitialValues: FeaturesConfigFormValues = {
  enName: '',
  deName: '',
  frName: '',
  itName: '',
  enDescription: '',
  deDescription: '',
  frDescription: '',
  itDescription: '',
  status: CONFIGURATION_STATUS.ACTIVE,
  selectionType: FEATURE_CONFIG_SELECTION_TYPE.SELECT,
  featureTypeId: null,
  featureGroupId: null,
  processAlloc: [],
  pfKapRequiresKpi: null,
  pfPgvRequiresKpi: null,
  kapFactsheetLevels: [],
  kapFactsheetLevelRequiresKpi: initialKapFactsheetLevelRequiresKpi,
}

const fetchHighestFeatureConfigSortNumber = (urqlClient: Client) => async () => {
  const { data, error } = await urqlClient
    .query<{
      feature_config_aggregate: Query_Root['feature_config_aggregate']
    }>(queryHighestFeatureConfigSortNumber, {})
    .toPromise()

  if (error || !data?.feature_config_aggregate.aggregate?.max?.sort_number) {
    throw new Error('Resource not found')
  }

  const fetchedHighestSortNumber = data.feature_config_aggregate.aggregate.max.sort_number
  return { fetchedHighestSortNumber }
}

const onCreate = () => {
  document.getElementById('feature-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const FeaturesConfigurationFeaturesCreatePage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const locale = useUserLocale()

  const [initialValues, setInitialValues] = useState<FeaturesConfigFormValues | null>(null)
  const [formDirty, setFormDirty] = useState<boolean | undefined>(undefined)

  const [featureTypes, setFeatureTypes] = useState<Feature_Type_Config[]>([])
  const [featureGroups, setFeatureGroups] = useState<Feature_Group_Config[]>([])

  useEffect(() => {
    setInitialValues(defaultInitialValues)
  }, [])

  const fetchFeatureTypes = useCallback(async () => {
    const { fetchedFeatureTypes } = await fetchAllFeatureTypesForFeaturesForm(urqlClient, locale)()
    return fetchedFeatureTypes
  }, [urqlClient, locale])

  const fetchFeatureGroups = useCallback(async () => {
    const { fetchedFeatureGroups } = await fetchAllFeatureGroups(urqlClient, locale)()
    return fetchedFeatureGroups
  }, [urqlClient, locale])

  const onBackHandler = useCallback(() => {
    navigate(ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeatures.params({}))
  }, [navigate])

  const handleSubmitLocal = useCallback(
    async (values: FeaturesConfigFormValues) => {
      if (values.featureTypeId == null) {
        notificationService.operationFailed()
        return
      }

      let newSortNumber: number
      try {
        const { fetchedHighestSortNumber } = await fetchHighestFeatureConfigSortNumber(urqlClient)()
        newSortNumber = fetchedHighestSortNumber + 1
      } catch (e) {
        notificationService.operationFailed()
        return
      }

      const hasKpiForLevels = Object.entries(values.kapFactsheetLevelRequiresKpi)
        .filter(([_, lvlValue]) => lvlValue !== null)
        .map(([lvlKey, _]) => lvlKey)

      const hasKpiForProcesses = [
        values.pfPgvRequiresKpi,
        hasKpiForLevels.length > 0 ? 'KAP' : null,
        values.pfKapRequiresKpi,
      ].filter((process) => process != null)

      const featureConfigInsertInput: Feature_Config_Insert_Input = {
        code: crypto.randomUUID(),
        names: { de: values.deName, en: values.enName, fr: values.frName, it: values.itName },
        tooltips: {
          de: values.deDescription ?? '',
          en: values.enDescription ?? '',
          fr: values.frDescription ?? '',
          it: values.itDescription ?? '',
        },
        feature_type_id: values.featureTypeId,
        feature_group_id: values.featureGroupId,
        processes: values.processAlloc,
        levels: values.kapFactsheetLevels,
        selection_type: values.selectionType,
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
        has_kpi_for_processes: hasKpiForProcesses,
        has_kpi_for_levels: hasKpiForLevels,
        sort_number: newSortNumber,
      }

      const { data, error } = await urqlClient
        .mutation<{
          insert_feature_config: Mutation_Root['insert_feature_config']
        }>(insertFeatureConfig, {
          featureConfigInsertInput,
        })
        .toPromise()

      if (!error && data?.insert_feature_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setFormDirty(false)
        onBackHandler()
      } else {
        notificationService.operationFailed()
      }
    },
    [notificationService, onBackHandler, urqlClient],
  )

  useEffect(() => {
    Promise.all([fetchFeatureTypes(), fetchFeatureGroups()])
      .then(([featureTypes, featureGroups]) => {
        setFeatureTypes(featureTypes)
        setFeatureGroups(featureGroups)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [fetchFeatureTypes, fetchFeatureGroups, notificationService])

  return (
    <ScreenLayout
      title={getMessage('label.configuration.features.create')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={<CreateButton origin="header" onClick={onCreate} />}
    >
      <PageLayout>
        <>
          {initialValues && (
            <FeaturesConfigurationFeaturesForm
              mode="CREATE"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
              featureTypes={featureTypes}
              featureGroups={featureGroups}
              formDirty={formDirty}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
